// Every glyph the shell draws, named once by purpose (never by shape) — see
// go-schedule's src/lib/icons.ts for the full rationale. The weight is
// written in exactly one place so a nav rail in one weight beside a toolbar
// in another can never happen by accident; it mirrors src/boot/icons.ts,
// which re-points Quasar's own internal icons (carets, ticks, …) at the same
// weight (Regular 400 — see that file for why).
const far = (name: string) => `fa-regular fa-${name}`

export const ICON = {
  // Account section (support-mediated claim, legacy-binding §5).
  claimTicket: far('key'),
  copy: far('copy'),
  menu: far('bars'),
  appMark: far('flag-checkered'),
  dashboard: far('gauge'),
  core: far('layer-group'),
  account: far('circle-user'),
  signOut: far('right-from-bracket'),
  pin: far('thumbtack'),
  selected: far('check'),
  error: far('triangle-exclamation'),
  impersonate: far('user-secret'),
  // Brand marks are inherently the brands weight — the one sanctioned
  // exception to the single-weight rule above (identity linking, setup).
  linkGoogle: 'fa-brands fa-google',
  linkApple: 'fa-brands fa-apple',
  // core-data module (services/portal/docs/modules/core-data.md)
  tenant: far('building'),
  competition: far('trophy'),
  event: far('calendar-days'),
  // Member documents reviewer queue (j55 DL-4).
  memberDocuments: far('id-card'),
  team: far('people-group'),
  university: far('graduation-cap'),
  person: far('user'),
  // The event participant roster (CO-15).
  roster: far('clipboard-list'),
  search: far('magnifying-glass'),
  rulesQuestion: far('circle-question'),
  rulesFeedback: far('comments'),
  rulesFaq: far('book-open-reader'),
  rulesReview: far('list-check'),
  rulesAssessment: far('sparkles'),
  rulesAssessmentReject: far('thumbs-down'),
  close: far('xmark'),
  chevronRight: far('chevron-right'),
  chevronDown: far('chevron-down'),
  info: far('circle-info'),
  hiddenToggle: far('eye-slash'),
  next: far('arrow-right'),
  vehicle: far('car-side'),
  // One team's entry into one event (issue #138 F1).
  participation: far('clipboard-check'),
  deadline: far('hourglass-half'),
  // The deadline definition editor (DL-7).
  deadlineSetup: far('calendar-pen'),
  // DL-8: copying a season's definitions into the next season.
  deadlineRollOver: far('forward'),
  // CO-9: the upload log across teams.
  deadlineLog: far('clock-rotate-left'),
  // The deadlines review queue (module brief §Review queue) — the worklist.
  deadlineQueue: far('list-check'),
  // An active review claim (fsgadmin/fsgnext#30, W1): the part is picked up
  // for review, which locks the TEAM's upload — a padlock, same glyph the
  // hotel module uses for its own locked state.
  deadlineClaimed: far('lock'),
  prev: far('arrow-left'),
  // CoreFilterBar / FacetList (ui-conventions.md #93 — mirrors go-schedule's
  // FilterBar/FacetList glyph set).
  expand: far('chevron-down'),
  collapse: far('chevron-up'),
  facetIncluded: far('circle-check'),
  facetExcluded: far('circle-minus'),
  facetUnset: far('circle'),
  reset: far('arrow-rotate-left'),
  // Per-row descent in hierarchical tables (ui-conventions.md #95: Tenant →
  // Competition → Event → Teams, and University → Teams). A hooked
  // turn-then-down glyph, deliberately not a plain arrow — chevronRight/next
  // already read as "open/navigate" elsewhere in this module, and #95 is
  // explicit that descend must not be confusable with "open row".
  descend: far('turn-down'),
  // core-admin-crud editing (specs/core-admin-crud/SPEC.md): the pencil is
  // the ONE per-section edit toggle (EditableSection.vue); add/delete/restore
  // are the lifecycle affordances (ui-conventions.md #74 — one gated button
  // per state transition).
  edit: far('pen'),
  add: far('plus'),
  merge: far('code-merge'),
  deleteAction: far('trash-can'),
  restore: far('trash-can-arrow-up'),
  // GlobalScopeBar (ui-conventions.md #97) — the shell-level Competition/
  // Season scope control, distinct from any per-page filter glyph.
  scope: far('globe'),
  // deadlines module matrix (services/portal/docs/modules/deadlines.md): the
  // per-cell most-recent-upload download affordance.
  download: far('download'),
  // deadlines matrix (DL-3): an issued result file, and the official's
  // action that issues or replaces one.
  resultDocument: far('file-certificate'),
  resultUpload: far('file-arrow-up'),
  // deadlines module matrix, TYPO3-density pass (owner ruling 2026-08-24):
  // one glyph per DeadlineState, color carrying the state (#61) — the label
  // moved to the tooltip only, so each state needs its OWN recognizable
  // shape, not just a color. Named per state, not per shape, even where two
  // states borrow icons.ts entries defined elsewhere for an unrelated
  // purpose (error/hiddenToggle) — this module's own semantics own these
  // keys independently.
  deadlinePassed: far('check'),
  deadlineInReview: far('magnifying-glass'),
  deadlineSubmitted: far('file-lines'),
  // tvsd module (services/portal/docs/modules/tvsd.md) — the judges' view of
  // the teams' technical vehicle system documentation.
  tvsd: far('file-contract'),
  tvsdColumns: far('table-columns'),
  tvsdExport: far('file-zipper'),
  deadlineCorrectionRequested: far('triangle-exclamation'),
  deadlineFailed: far('xmark'),
  // Not `fa-square` (owner fix 2026-08-24 — "what does that do?"): an empty
  // checkbox glyph reads as an interactive control in a dense grid of
  // otherwise-clickable rows. A dimmed dash reads as "nothing here yet"
  // without inviting a click.
  deadlineNotUploaded: far('minus'),
  // hotel module (specs/archive/hotel/SPEC.md §8.5) — the nine sub-pages plus the
  // affordances only this module has. Named per PURPOSE, as everywhere else
  // in this file, so a glyph swap is one edit here.
  hotel: far('hotel'),
  hotelUserData: far('address-card'),
  hotelBeds: far('bed'),
  hotelTodo: far('list-check'),
  hotelRun: far('paper-plane'),
  hotelReport: far('table-list'),
  hotelRooms: far('door-open'),
  hotelLocked: far('lock'),
  hotelUnlocked: far('lock-open'),
  hotelSend: far('paper-plane'),
  hotelSkip: far('forward'),
  hotelRegenerate: far('arrows-rotate'),
  hotelMarkSent: far('circle-check'),
  hotelCancelRun: far('ban'),
  hotelPin: far('thumbtack'),
  hotelExport: far('file-export'),
  hotelColumns: far('table-columns'),
  hotelMail: far('envelope'),
  // rbac module (services/portal/docs/modules/rbac.md) — the platform.admin
  // access-control surfaces. Named per purpose: the module is "who may do
  // what", a group is a bundle of people, an assignment is the resolved
  // answer, a grant is one hand-issued row.
  rbac: far('shield-halved'),
  rbacGroup: far('users-rectangle'),
  rbacAssignment: far('sitemap'),
  rbacGrant: far('key'),
  rbacService: far('robot'),
  // The hierarchy graph (fsgadmin/fsgnext#3): the module's group DAG
  // drawn as a picture, and its fit-viewport control.
  rbacGraph: far('diagram-project'),
  rbacGraphFit: far('arrows-to-dot'),
  // reports module (services/portal/docs/modules/reports.md) — the
  // trusted-tester problem-report surfaces. The bug is the module; the
  // crosshairs are the element picker's "aim at the page" affordance; the
  // raised hand is the needs-human triage queue.
  report: far('bug'),
  reportPick: far('crosshairs'),
  reportNeedsHuman: far('hand'),
  // Problem reports panel (prototype parity T7): the evidence links (session
  // replay, trace, fixing commit), Comment, Withdraw.
  rptReplay: far('play'),
  rptTrace: far('magnifying-glass'),
  rptFix: far('check'),
  rptComment: far('message'),
  rptWithdraw: far('trash-can'),
  // tickets module (services/portal/docs/modules/tickets.md) — the operator
  // Ticket Center. Named per purpose: the badge is the module; the printer is
  // both the desk's per-row DYMO action and the bulk print surface (two keys,
  // one glyph — a swap stays one edit each); the QR glyph is the scan/lookup
  // field; the clock-rotate is the audit trail.
  ticket: far('ticket'),
  ticketPrint: far('print'),
  ticketBulk: far('print'),
  ticketType: far('tags'),
  ticketAudit: far('clock-rotate-left'),
  // audit module (services/portal/docs/modules/audit.md)
  audit: far('clock-rotate-left'),
  ticketScan: far('qrcode'),
  // sponsors module (services/portal/docs/modules/sponsors.md)
  sponsor: far('handshake'),
  // expenses module (services/portal/docs/modules/expenses.md) — the
  // reimbursement submitter + reviewer surfaces. Named per purpose: the
  // receipt is the module; the file-arrow is "attach a scan"; the image is a
  // captured/attached scan; the magnifier-dollar is the review queue; the
  // money-transfer is the SEPA payout run; the QR is the EPC/Girocode a
  // reviewer scans to pay one envelope; the file-csv is the bookkeeper
  // handoff export; the arrows-rotate is rendering the cover sheet again.
  expense: far('receipt'),
  expenseUpload: far('file-arrow-up'),
  receiptScan: far('image'),
  expenseReview: far('magnifying-glass-dollar'),
  expensePayout: far('money-bill-transfer'),
  expenseQr: far('qrcode'),
  expenseExport: far('file-csv'),
  expenseRegenerateCover: far('arrows-rotate'),
  expenseBank: far('building-columns'),
  // finance-admin module (services/portal/docs/modules/finance-admin.md) —
  // route + nav entries are wired here (the shell files are one owner's);
  // the module's own pages live in src/modules/finance/.
  finance: far('building-columns'),
  financeOrders: far('file-invoice-dollar'),
  financeQueue: far('inbox'),
  financeRefunds: far('rotate-left'),
  // rules module (services/portal/docs/modules/rules.md, fsgadmin/fsgnext#68) —
  // the rulebook history explorer. The book is the module; the tree is the
  // chapter outline; the branch is a rule's lineage across editions; the
  // broken link is a number BREAK (the same printed number meaning a
  // different rule in the next edition), which is the one thing in this
  // module a reader must not miss.
  rules: far('book'),
  rulesOutline: far('list-tree'),
  rulesLineage: far('code-branch'),
  rulesBreak: far('link-slash'),
  rulesPdf: far('file-pdf'),
  /** A link a language model adjudicated rather than a rule derived (B18:
   *  LLM-adjudicated links are visibly marked). */
  rulesAdjudicated: far('wand-magic-sparkles'),
  // docs module (services/portal/docs/modules/docs.md, fsgadmin/fsgnext#69,
  // register M) — the public-website documents manager (TYPO3 fileadmin ->
  // docstore public tier). The folder tree is the browse rail; upload/publish
  // are the module's two write verbs; the globe marks anything visitor-facing.
  docs: far('folder-open'),
  docsFolder: far('folder'),
  docsFile: far('file'),
  docsUpload: far('cloud-arrow-up'),
  docsPublications: far('globe'),
  docsPublish: far('share-nodes'),
  docsUnpublish: far('link-slash'),
  docsMove: far('folder-tree'),
  docsReplace: far('rotate'),
  docsDelete: far('trash'),
  docsHistory: far('clock-rotate-left'),
  docsReorder: far('grip-lines'),
  // scoring module (services/portal/docs/modules/scoring.md, fsgadmin/fsgnext#85,
  // WP10) — the officials' scoring desk. Named per purpose like everything
  // else in this file, and owned by this module independently of any glyph it
  // happens to share with another (the deadlineState precedent above).
  //
  // The module glyph is the CALCULATOR, deliberately not the chequered flag:
  // `appMark` already owns that glyph as the portal's brand mark, and a nav
  // entry drawn with the brand mark reads as "home", not as a module. What
  // this module is, is the machine that turns recorded inputs into points.
  //
  // The podium is the standings; the numeric field is the input desk; the
  // clock-rotate is the write-ahead log (kickoff ruling 4 — the History
  // surface is a first-class citizen, not a debug tab); the gavel is the
  // penalty-case workflow; the certificate is a frozen, versioned
  // publication; the ordered list is the running order; the medal is a
  // special award; the chart-magnifier is the evaluation-trace explorer
  // (C8 — "the traces need a rich portal surface"); the sliders are the
  // configuration plane; the clipboard is the two audit ledgers.
  scoring: far('calculator'),
  scoringResults: far('ranking-star'),
  scoringInputs: far('input-numeric'),
  scoringHistory: far('clock-rotate-left'),
  scoringPenalties: far('gavel'),
  scoringPublications: far('file-certificate'),
  scoringRunorder: far('list-ol'),
  scoringAwards: far('award'),
  scoringPreviewPdf: far('file-pdf'),
  scoringTraces: far('magnifying-glass-chart'),
  scoringConfig: far('sliders'),
  scoringAudit: far('clipboard-list'),
  // The Award ceremony tool (services/portal/docs/modules/ceremony.md, #163).
  // Statics feedback booking (services/portal/docs/modules/statics-feedback.md, #165).
  staticsFeedback: far('comments'),
  staticsFeedbackSchedule: far('calendar-clock'),
  staticsFeedbackSetup: far('sliders'),
  staticsFeedbackPrint: far('print'),
  staticsFeedbackBook: far('calendar-plus'),
  staticsFeedbackRebook: far('calendar-pen'),
  staticsFeedbackCancel: far('calendar-xmark'),
  staticsFeedbackGenerate: far('table-cells'),
  staticsFeedbackExport: far('file-csv'),
  staticsFeedbackCopy: far('copy'),
  staticsFeedbackAdd: far('plus'),
  staticsFeedbackEdit: far('pen'),
  staticsFeedbackDelete: far('trash-can'),
  ceremony: far('presentation-screen'),
  ceremonyProgram: far('list-ol'),
  ceremonySeason: far('calendar-star'),
  ceremonyRunOfShow: far('clipboard-list-check'),
  ceremonyThemes: far('palette'),
  ceremonyAudit: far('clipboard-list'),
  ceremonyDrag: far('grip-dots-vertical'),
  ceremonyAddSlide: far('plus'),
  ceremonyBuild: far('hammer'),
  ceremonyWinners: far('trophy'),
  ceremonyImport: far('file-import'),
  ceremonyPhoto: far('camera'),
  ceremonyOverride: far('user-pen'),
  ceremonyCheck: far('play'),
  ceremonyPrint: far('print'),
  ceremonyUpload: far('file-zipper'),
  ceremonyPdf: far('file-pdf'),
  ceremonyPreview: far('image'),
  ceremonyPptx: far('file-powerpoint'),
  ceremonyXlsx: far('file-excel'),
  ceremonyFinal: far('flag-checkered'),
  ceremonyProblem: far('triangle-exclamation'),
  ceremonyCopy: far('copy'),
  ceremonyReload: far('arrows-rotate'),
  ceremonyDelete: far('trash-can'),
  ceremonyEdit: far('pen'),
  ceremonyUp: far('arrow-up'),
  ceremonyDown: far('arrow-down'),
  ceremonyZoomIn: far('magnifying-glass-plus'),
  ceremonyRotate: far('rotate-right'),
  ceremonyCenter: far('crosshairs'),
  ceremonyDocs: far('book'),
  ceremonyDownload: far('download'),
  // notification module (services/portal/docs/modules/notification-admin.md,
  // NO-4): the envelope is the Messages entry; the open envelope with text
  // is the admin mail log (what was sent, to whom); the paper plane sends a
  // test mail; the arrows re-queue a DEAD delivery.
  messages: far('envelope'),
  messagesAdmin: far('envelope-open-text'),
  messageSend: far('paper-plane'),
  messageRetry: far('arrows-rotate'),
  // press module (services/portal/docs/modules/press.md, j55 HT-11/HT-12).
  press: far('newspaper'),
  pressQueue: far('list-check'),
  pressApply: far('id-card'),
  pressStartReview: far('magnifying-glass'),
  pressApprove: far('circle-check'),
  pressDecline: far('circle-xmark'),
  pressNoDocument: far('triangle-exclamation'),
  pressUpload: far('file-arrow-up'),

  // Redesign shell (ui-conventions.md #158): the rail's group headers, the
  // site menu and the account menu's appearance switch.
  railGroupTeam: far('car'),
  railGroupDocuments: far('file-lines'),
  railGroupScoring: far('stopwatch'),
  railGroupOffice: far('calendar'),
  railGroupFinance: far('receipt'),
  railGroupRules: far('book'),
  railGroupPress: far('globe'),
  railGroupPlatform: far('layer-group'),
  railCollapse: far('angles-left'),
  railExpand: far('angles-right'),
  publicSite: far('house'),
  externalLink: far('arrow-up-right-from-square'),
  // A team's own website, on its public profile (prototype `team`).
  siteTeamWebsite: far('globe'),
  themeLight: far('sun'),
  themeDark: far('moon'),
  themeAuto: far('circle-half-stroke'),
  // Account menu (prototype `.um-menu`).
  password: far('key'),
  appearance: far('palette'),

  // Workspace (ui-conventions.md #154): panel chrome, the Layout menu and the
  // dock targets of the Move menu.
  wsLayout: far('table-columns'),
  wsGrip: far('grip-dots-vertical'),
  wsMove: far('ellipsis'),
  wsMaximize: far('up-right-and-down-left-from-center'),
  wsHide: far('eye-slash'),
  wsDockLeft: far('arrow-left-to-line'),
  wsDockRight: far('arrow-right-to-line'),
  wsDockTop: far('arrow-up-to-line'),
  wsDockBottom: far('arrow-down-to-line'),
  wsFloat: far('window-restore'),
  wsReset: far('arrow-rotate-left'),
  wsTools: far('toolbox'),
  help: far('circle-question'),

  // Deadlines matrix and review page (prototype parity T2): the scoped-empty
  // card, the panels Detail / Review history / How the pool works, and the
  // way back from the review page.
  scopeEmpty: far('filter'),
  deadlineDetail: far('file-lines'),
  deadlineHistory: far('clock'),
  deadlinePoolHelp: far('book'),
  deadlineBackToMatrix: far('table-columns'),
  deadlinePool: far('globe'),
  deadlineSubmitReview: far('paper-plane'),

  // Scoring office › Publications (prototype parity T3): the preflight box's
  // flag and the publish acts.
  scoringPubPreflight: far('flag'),
  scoringPubPublish: far('paper-plane'),

  // Scoring office › Configuration and › Audit (prototype parity T3): the
  // detail and entry panels, your-access, the lock acts and re-derive.
  scoringCfgDetail: far('pen-to-square'),
  scoringCfgLock: far('lock'),
  scoringCfgUnlock: far('lock-open'),
  scoringCfgRederive: far('arrows-rotate'),
  scoringAudEntry: far('file-lines'),
  scoringAudAccess: far('key'),

  // Scoring office › Penalties (prototype parity T3): the Case, Penalty,
  // Protest and Decision templates panels, and the publish act.
  scoringPenCase: far('flag'),
  scoringPenPenalty: far('shield-halved'),
  scoringPenProtest: far('scale-balanced'),
  scoringPenTemplates: far('book'),
  scoringPenPublish: far('paper-plane'),

  // Scoring › Inputs (prototype parity T3): the desk's panels, its Tools
  // strip and the head's publish act.
  scoringInRun: far('stopwatch'),
  scoringInCase: far('flag'),
  scoringInLaps: far('clock'),
  scoringInOrder: far('list-ol'),
  scoringInLocks: far('lock'),
  scoringInParams: far('sliders'),
  scoringInScore: far('trophy'),
  scoringInImport: far('file-arrow-up'),
  scoringInDq: far('ban'),
  scoringInExport: far('file-csv'),
  scoringInPublish: far('paper-plane'),

  // Scoring office › Awards / Traces (prototype parity T3): the finalists
  // panel and the formula-variants warning.
  scoringAwFinalists: far('star'),
  scoringTrVariants: far('flag'),

  // Scoring office › Running order (prototype parity T3): publish, the ↑/↓
  // move arrows, the printed note and the version/template file.
  runorderPublish: far('paper-plane'),
  runorderUp: far('arrow-up'),
  runorderDown: far('arrow-down'),
  runorderNote: far('file-lines'),
  runorderTemplate: far('file-lines'),

  // Event office › Sponsors (prototype parity T4): the head's partner tag,
  // the panel's ⋯ menu, a contact row and the allocations export.
  sponsorsPartners: far('star'),
  sponsorsMore: far('ellipsis'),
  sponsorsContact: far('user'),
  sponsorsAllocExport: far('file-csv'),
  // #168 register AP: the Profile panel (logo upload, the wall preview), the
  // Wall view and its copy, and a partner page's careers button.
  sponsorsProfile: far('id-card'),
  sponsorsWall: far('grip'),
  sponsorsLogoUpload: far('upload'),
  sponsorsWallCopy: far('copy'),
  // #182: the Wall view's "Export SVG…".
  sponsorsWallExport: far('file-image'),
  partnerCareers: far('briefcase'),

  // Event office › Tickets › Bulk print / Audit log (prototype parity T4):
  // the Entitlements and Print runs panels, the run's PDF document, the
  // audit Filter and Change panels.
  tkBulkEnts: far('users'),
  tkBulkRuns: far('clock-rotate-left'),
  tkBulkDoc: far('file-pdf'),
  tkAuditFilter: far('filter'),
  tkAuditChange: far('file-lines'),

  // Event office › Teams (prototype parity T4): the team panel's ⋯ menu, the
  // Public page link, the Registration panel and the Export tool.
  teamsMore: far('ellipsis'),
  teamsPublicPage: far('globe'),
  teamsRegistration: far('file-signature'),
  teamsExport: far('download'),

  // Event office › Tickets › Ticket center (prototype parity T4): the panel's
  // ⋯ menu, the print queue (add, remove, print all), the judge+team
  // conflict flag and the two export tools.
  tkcMore: far('ellipsis'),
  tkcQueue: far('list-check'),
  tkcQueueAdd: far('plus'),
  tkcRemove: far('xmark'),
  tkcPrintAll: far('paper-plane'),
  tkcConflict: far('flag'),
  tkcExportCsv: far('file-csv'),
  tkcExportXls: far('file-excel'),

  // Event office › Volunteer roster and Core › Roster (prototype parity T4):
  // the decisions, the panels and the Tools strip.
  rosterApprove: far('check'),
  rosterApplication: far('id-card'),
  rosterJobs: far('users'),
  rosterStats: far('chart-simple'),
  rosterColumns: far('table-columns'),
  rosterExport: far('download'),
  rosterVolunteers: far('calendar'),

  // Event office › Hotel reports (prototype parity T4: Assigned users,
  // Assigned users to-do, Guests in rooms (report)): the Problem panel and
  // the Export tool.
  hotelRepProblem: far('flag'),
  hotelRepExport: far('download'),

  // Event office › Hotel shell + Edit user data / Guests in rooms / Booking
  // to-do (prototype parity T4): the Room panel's ⋯ menu and the to-do
  // card's assign-a-room action.
  hotelShellMore: far('ellipsis'),
  hotelShellAssignRoom: far('bed'),

  // Rules › ticket page (prototype parity T5): the Tools strip, the panels
  // and the timeline/assessment controls.
  rlTkMessage: far('message'),
  rlTkInternal: far('lock'),
  rlTkRequest: far('rotate-left'),
  rlTkEdit: far('pen'),
  rlTkAssign: far('user'),
  rlTkRefs: far('book'),
  rlTkTier: far('eye'),
  rlTkTag: far('flag'),
  rlTkAck: far('check'),
  rlTkExpert: far('users'),
  rlTkImplement: far('sitemap'),
  rlTkFaq: far('globe'),
  rlTkClose: far('circle-check'),
  rlTkReopen: far('arrow-rotate-left'),
  rlTkExport: far('download'),
  rlTkDelete: far('trash-can'),
  rlTkPeople: far('users'),
  rlTkFiles: far('paperclip'),
  rlTkRepo: far('code-branch'),
  rlTkRelated: far('comments'),
  rlTkSend: far('paper-plane'),
  rlTkRerun: far('play'),
  rlTkReadOnly: far('lock'),

  // Rules › Questions / Feedback lists (prototype parity T5): Tools strip,
  // panels, the one-click Acknowledge and the panel's composer.
  rlLsNew: far('plus'),
  rlLsAsk: far('circle-question'),
  rlLsFileFeedback: far('flag'),
  rlLsExport: far('download'),
  rlLsDetailQ: far('message'),
  rlLsDetailF: far('comments'),
  rlLsExperts: far('users'),
  rlLsAcknowledge: far('check'),
  rlLsSend: far('paper-plane'),
  rlLsMore: far('ellipsis'),
  rlLsOpen: far('up-right-and-down-left-from-center'),
  rlLsLegend: far('circle-info'),
  rlLsRemove: far('xmark'),
  rlLsInternal: far('lock'),

  // Finance › Orders and the order page (prototype parity T5): the Tools
  // strip, the Order panel and the order page's panels.
  finOrdNew: far('plus'),
  finOrdColumns: far('table-columns'),
  finOrdExport: far('download'),
  finOrdOpen: far('up-right-and-down-left-from-center'),
  finOrdBack: far('chevron-left'),
  finOrdPanel: far('receipt'),
  finOrdBuyer: far('address-card'),
  finOrdInvoices: far('file-invoice'),
  finOrdPayments: far('clock'),
  finOrdWarn: far('flag'),
  finOrdLimit: far('circle-info'),
  finOrdAddress: far('pen'),
  finOrdApprove: far('check'),
  finOrdCredit: far('receipt'),
  finOrdRefund: far('rotate-left'),
  finOrdSettle: far('check'),
  finOrdCheckout: far('link'),
  finOrdCancel: far('xmark'),
  finOrdNone: far('circle-info'),

  // Rules › FAQ, My queue and Experts (prototype parity T5): the reviewers
  // panel and the "open the full ticket" link of the queue's ticket panel.
  rlxReviewers: far('users'),
  rlxTicket: far('message'),
  rlxOpenTicket: far('arrow-up-right-from-square'),

  // Documents › Publications (prototype parity T6): the Pages panel, the
  // "How publishing works" help panel, Preview, entry reorder and Remove.
  docsPubPages: far('sitemap'),
  docsPubHelp: far('book'),
  docsPubPreview: far('eye'),
  docsPubUp: far('arrow-up'),
  docsPubDown: far('arrow-down'),
  docsPubRemove: far('xmark'),

  // Press › Accreditations and Apply (prototype parity T6): the Columns and
  // Export tools, the missing-document pill, the applicant line and the
  // Media library panel with its outbound link.
  pressColumns: far('table-columns'),
  pressExport: far('download'),
  pressMissing: far('flag'),
  pressApplicant: far('user'),
  pressMedia: far('image'),
  pressMediaLink: far('arrow-up-right-from-square'),

  // Documents › Browse (prototype parity T6): the tree's root row, the
  // Columns tool, the file panel's ⋯ menu and its "On the website" rows.
  docbrAll: far('layer-group'),
  docbrColumns: far('table-columns'),
  docbrMore: far('ellipsis'),
  docbrWeb: far('globe'),

  // Access › Groups / Graph (prototype parity T7): the group panel's ⋯ menu,
  // the Columns and "Open in graph" tools, the legend panel.
  rbacGxMore: far('ellipsis'),
  rbacGxColumns: far('table-columns'),
  rbacGxOpenGraph: far('diagram-project'),
  rbacGxLegend: far('book'),
  rbacGxFit: far('expand'),

  // Platform › Audit log (prototype parity T7): the Change panel, the
  // "More filters" chip and the Export tool.
  auditCompare: far('code-compare'),
  auditFilter: far('filter'),
  auditExport: far('download'),

  // Platform › Mail log (prototype parity T7): the Preview, Env policy and
  // Sender panels, the "Person, time" chip, Columns and attachments.
  maillogPreview: far('envelope'),
  maillogPolicy: far('shield'),
  maillogSender: far('at'),
  maillogFilter: far('filter'),
  maillogColumns: far('table-columns'),
  maillogAttachment: far('paperclip'),

  // Access › Assignments / Direct grants / Service principals (prototype
  // parity T7): the Export tool, the panel's "Show only" (person mode), a
  // group membership row and its remove control.
  accExport: far('download'),
  accShowOnly: far('filter'),
  accGroup: far('users'),
  accRemove: far('xmark'),

  // Core › Events (prototype parity T7): the Hierarchy panel and the
  // Columns / Export tools.
  coreEvTree: far('layer-group'),
  coreEvColumns: far('table-columns'),
  coreEvExport: far('download'),

  // CMS pages (#160 WP7): a gated menu entry or page, the sign-in offer on a
  // lock card or placeholder, a download row, a data-module box, the way home.
  cmsLocked: far('lock'),
  cmsSignIn: far('right-to-bracket'),
  cmsFile: far('file-lines'),
  cmsDownload: far('download'),
  cmsModule: far('layer-group'),
  cmsHome: far('house'),
  cmsPage: far('file'),
  // The CMS inline editor (#160 WP8a, prototype v60): the drag grip of a box,
  // column or item; a box with columns; the text toolbar; the image picker
  // and bar; preview, publish, done, history.
  cmsGrip: far('grip-dots-vertical'),
  cmsColumns: far('table-columns'),
  cmsText: far('pen'),
  cmsBold: far('bold'),
  cmsItalic: far('italic'),
  cmsLink: far('link'),
  cmsListUl: far('list-ul'),
  cmsListOl: far('list-ol'),
  cmsTable: far('table'),
  cmsImage: far('image'),
  // A VIDEO item (#160 AF42): the add menu, its head and the empty frame.
  cmsVideo: far('play'),
  cmsZoom: far('magnifying-glass-plus'),
  cmsUndo: far('arrow-rotate-left'),
  cmsRedo: far('arrow-rotate-right'),
  cmsClear: far('eraser'),
  cmsEye: far('eye'),
  cmsPublish: far('upload'),
  cmsDone: far('check'),
  cmsHistory: far('clock-rotate-left'),
  cmsUp: far('chevron-up'),
  cmsDown: far('chevron-down'),
  cmsSave: far('floppy-disk'),
  // The hero photo's "Position & size" (#160 AF41).
  cmsHeroPosition: far('crosshairs'),
  // The Website tool (#160 WP8b, prototype `tool-cms`): the module, its tabs
  // (pages tree, audiences, redirects, images, audit), tree node kinds and
  // the page detail's acts.
  website: far('sitemap'),
  websitePages: far('sitemap'),
  websiteAudiences: far('users'),
  websiteRedirects: far('arrow-right-arrow-left'),
  websiteImages: far('images'),
  websiteAudit: far('list-check'),
  websiteSection: far('folder'),
  websiteLink: far('link'),
  websiteSpacer: far('grip-lines-vertical'),
  websiteFooter: far('grip-lines'),
  websiteHidden: far('eye-slash'),
  websiteUpload: far('cloud-arrow-up'),
  websiteDelete: far('trash-can'),
  websiteOpen: far('arrow-up-right-from-square'),
  // The image library's folders (#160 Amendment 2): a folder, a new one,
  // renaming, and "Move to…" (images or a folder).
  websiteFolder: far('folder'),
  websiteFolderNew: far('folder-plus'),
  websiteRename: far('pen'),
  websiteMove: far('folder-tree'),
  // The image picker's Shutterbase tab: its tag filter and the per-image
  // "Show description" toggle.
  websiteSbTags: far('tags'),
  websiteSbDescription: far('circle-info'),
  // The News module (#162): the tool and its tabs, the article states'
  // actions, the RSS feed and the share links (brands, AH12).
  news: far('bullhorn'),
  newsArticles: far('rectangle-list'),
  newsCategories: far('tags'),
  newsSchedule: far('clock'),
  newsUnpublish: far('eye-slash'),
  newsPreview: far('eye'),
  newsSlug: far('link'),
  newsCrop: far('crop-simple'),
  newsDetails: far('sliders'),
  newsImages: far('images'),
  newsLinks: far('arrow-up-right-from-square'),
  newsEarlier: far('chevron-left'),
  newsLater: far('chevron-right'),
  newsMakeList: far('star'),
  newsFile: far('file-arrow-up'),
  newsRelated: far('newspaper'),
  newsRss: far('square-rss'),
  newsArchive: far('box-archive'),
  newsShareMail: far('envelope'),
  newsShareLinkedin: 'fa-brands fa-linkedin',
  newsShareX: 'fa-brands fa-x-twitter',
  newsShareFacebook: 'fa-brands fa-facebook',
  // World › Competitions (#170): a table row's "Show on the map".
  siteWorldShowOnMap: far('location-dot'),
  // The designed error page (core/ErrorPage).
  errorRetry: far('arrow-rotate-right'),
  errorBack: far('chevron-left'),
  // Announcements (#164): the tool, the board's notification button, a version's history.
  announcements: far('megaphone'),
  announcementPush: far('bell'),
  announcementHistory: far('clock-rotate-left'),
  // The event map reader (#169, services/portal/docs/modules/map.md).
  eventMap: far('map-location-dot'),
  // The map editor (#169 WP8a).
  mapEditor: far('pen-ruler'),
  mapImport: far('file-import'),
  mapPlans: far('compass-drafting'),
  mapShapes: far('shapes'),
  mapTrack: far('route'),
  mapSettings: far('gear'),
  mapReferences: far('draw-square'),
  mapOverlays: far('clone'),
  mapSelect: far('arrow-pointer'),
  mapPolygon: far('draw-polygon'),
  mapLine: far('bezier-curve'),
  mapPoint: far('location-dot'),
  mapRect: far('vector-square'),
  // the map editor's path tool strip (#169, owner request 2026-10-25)
  mapPointSelect: far('hand-pointer'),
  mapAddPoint: far('square-plus'),
  mapDeletePoint: far('square-minus'),
  mapConvertPoint: far('pen-nib'),
  mapMergePoints: far('arrows-to-dot'),
  mapJoinPaths: far('link'),
  mapSelectConnected: far('route'),
  mapCombine: far('object-union'),
  mapSplitPath: far('scissors'),
  mapClosePath: far('circle-dashed'),
  mapSimplify: far('feather'),
  mapReverse: far('arrow-right-arrow-left'),
  mapUndo: far('arrow-rotate-left'),
  mapRedo: far('arrow-rotate-right'),
  mapSave: far('floppy-disk'),
  mapPublish: far('upload'),
  mapReload: far('arrows-rotate'),
  mapDelete: far('trash-can'),
  mapEye: far('eye'),
  mapEyeOff: far('eye-slash'),
  mapEdit: far('pen'),
  mapAdd: far('plus'),
  mapVenue: far('location-pin'),
  mapIcons: far('icons'),
  mapCategories: far('tags'),
  mapProperties: far('sliders'),
  mapZoomIn: far('plus'),
  mapZoomOut: far('minus'),
  mapLayers: far('layer-group'),
  // the editor's outline view (#169, owner request 2026-10-26)
  mapOutline: far('vector-polygon'),
  mapLocate: far('location-crosshairs'),
  mapMeasure: far('ruler'),
  mapPdf: far('file-pdf'),
  mapPhase: far('calendar-day'),
  mapFloor: far('stairs'),
  // floors and rooms (#184): the Buildings mode, a room, its use catalogue
  mapBuildings: far('building'),
  mapRoom: far('door-open'),
  mapRoomUses: far('list-ul'),
  mapCapacity: far('users'),
  mapBack: far('chevron-left'),
  mapPit: far('flag-checkered'),
  mapPlace: far('location-dot'),
  mapMarker: far('map-pin'),
  mapTeam: far('people-group'),
  mapOffline: far('cloud-slash'),
  mapShowPit: far('map-location-dot'),
  mapLink: far('link'),
  // The map editor's tools (#169 WP8b): references, import wizard, plan alignment.
  mapWarning: far('triangle-exclamation'),
  mapUpload: far('cloud-arrow-up'),
  mapCalibrate: far('crosshairs'),
  mapClassify: far('list-check'),
  mapPreview: far('eye'),
  mapApply: far('check-double'),
  mapDiff: far('code-compare'),
  mapClip: far('crop-simple'),
  mapDimension: far('ruler-combined'),
  mapRotate: far('rotate-right'),
  mapFlip: far('arrows-left-right'),
  mapPropose: far('wand-magic-sparkles'),
  mapCorners: far('vector-square'),
  mapHistory: far('clock-rotate-left'),
  mapFile: far('file'),
} as const
